// client/src/components/parent/CheckInForChild.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Slider,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const CheckInForChild = ({ token }) => {
  const { childId } = useParams();
  const navigate = useNavigate();

  const [child, setChild] = useState(null);
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadCheckInData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId, token]);

  const loadCheckInData = async () => {
    setLoading(true);
    setError('');
    try {
      // Get child info and template from the check-in endpoint
      const response = await api.getMyChildrenForCheckIn(token);
      const childData = response.children?.find(c => c._id === childId);

      if (!childData) {
        setError('Child not found or you do not have permission to submit check-ins for this child.');
        setLoading(false);
        return;
      }

      setChild(childData);

      // Use the template from the response if available, otherwise use default parent template
      const templateData = response.template || childData.checkInTemplate || getDefaultParentTemplate();
      setTemplate(templateData);

      // Initialize responses with default values
      const initialResponses = {};
      if (templateData?.fields) {
        templateData.fields.forEach(field => {
          if (field.fieldType === 'numeric_scale') {
            initialResponses[field.id] = Math.ceil((field.config?.min || 1 + field.config?.max || 5) / 2);
          } else if (field.fieldType === 'boolean') {
            initialResponses[field.id] = false;
          } else if (field.fieldType === 'multi_select') {
            initialResponses[field.id] = [];
          } else {
            initialResponses[field.id] = '';
          }
        });
      }
      setResponses(initialResponses);
    } catch (err) {
      console.error('Error loading check-in data:', err);
      setError(err.message || 'Failed to load check-in data');
    } finally {
      setLoading(false);
    }
  };

  // Fallback template if none is returned from the server
  const getDefaultParentTemplate = () => ({
    key: 'adhd-child-parent-daily',
    displayName: 'ADHD Parent Daily Observation',
    fields: [
      {
        id: 'attention_at_home',
        label: 'How well did your child focus on tasks at home today?',
        fieldType: 'numeric_scale',
        config: { min: 1, max: 5, step: 1 },
        required: true,
        helpText: '1 = could not focus at all, 5 = focused very well',
        order: 1,
      },
      {
        id: 'homework_behavior',
        label: 'How was homework time today?',
        fieldType: 'single_select',
        config: {
          options: [
            { value: 'smooth', label: 'Smooth - no issues' },
            { value: 'some_struggles', label: 'Some struggles' },
            { value: 'difficult', label: 'Very difficult' },
            { value: 'refused', label: 'Refused to do homework' },
            { value: 'no_homework', label: 'No homework today' },
          ],
        },
        required: true,
        order: 2,
      },
      {
        id: 'hyperactivity_level',
        label: 'How would you rate your child\'s activity/restlessness level today?',
        fieldType: 'numeric_scale',
        config: { min: 1, max: 5, step: 1 },
        required: true,
        helpText: '1 = very hyperactive/restless, 5 = calm and settled',
        order: 3,
      },
      {
        id: 'mood',
        label: 'How was your child\'s overall mood today?',
        fieldType: 'numeric_scale',
        config: { min: 1, max: 5, step: 1 },
        required: true,
        helpText: '1 = very upset/irritable, 5 = happy and content',
        order: 4,
      },
      {
        id: 'notes',
        label: 'Any additional observations or concerns?',
        fieldType: 'text',
        config: { maxLength: 500, placeholder: 'Optional: Notable events, triggers, or concerns...' },
        required: false,
        order: 5,
      },
    ],
  });

  const handleResponseChange = (fieldId, value) => {
    setResponses(prev => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleMultiSelectChange = (fieldId, optionValue, checked) => {
    setResponses(prev => {
      const current = prev[fieldId] || [];
      if (checked) {
        // If "none" is selected, clear others. If other selected, remove "none"
        if (optionValue === 'none') {
          return { ...prev, [fieldId]: ['none'] };
        } else {
          return { ...prev, [fieldId]: [...current.filter(v => v !== 'none'), optionValue] };
        }
      } else {
        return { ...prev, [fieldId]: current.filter(v => v !== optionValue) };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      const missingFields = template.fields
        .filter(f => f.required && !responses[f.id] && responses[f.id] !== 0)
        .map(f => f.label);

      if (missingFields.length > 0) {
        setError(`Please complete all required fields: ${missingFields.join(', ')}`);
        setSubmitting(false);
        return;
      }

      const checkInData = {
        templateKey: template.key,
        responses,
        informantType: 'parent',
      };

      await api.submitCheckInForChild(childId, checkInData, token);
      setSuccess('Check-in submitted successfully!');

      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/parent/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Error submitting check-in:', err);
      setError(err.message || 'Failed to submit check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = responses[field.id];

    switch (field.fieldType) {
      case 'numeric_scale':
        return (
          <Box key={field.id} sx={{ mb: 4 }}>
            <Typography gutterBottom>
              {field.label}
              {field.required && <span style={{ color: 'error.main' }}> *</span>}
            </Typography>
            {field.helpText && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {field.helpText}
              </Typography>
            )}
            <Slider
              value={value || field.config?.min || 1}
              onChange={(e, newValue) => handleResponseChange(field.id, newValue)}
              min={field.config?.min || 1}
              max={field.config?.max || 5}
              step={field.config?.step || 1}
              marks
              valueLabelDisplay="auto"
              sx={{ mt: 2 }}
            />
          </Box>
        );

      case 'single_select':
        return (
          <FormControl key={field.id} component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">
              {field.label}
              {field.required && <span style={{ color: 'error.main' }}> *</span>}
            </FormLabel>
            <RadioGroup
              value={value || ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value)}
            >
              {field.config?.options?.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'multi_select':
        return (
          <FormControl key={field.id} component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">
              {field.label}
              {field.required && <span style={{ color: 'error.main' }}> *</span>}
            </FormLabel>
            <FormGroup>
              {field.config?.options?.map(option => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={(value || []).includes(option.value)}
                      onChange={(e) => handleMultiSelectChange(field.id, option.value, e.target.checked)}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
          </FormControl>
        );

      case 'boolean':
        return (
          <FormControl key={field.id} component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">
              {field.label}
              {field.required && <span style={{ color: 'error.main' }}> *</span>}
            </FormLabel>
            <RadioGroup
              value={value === true ? 'yes' : value === false ? 'no' : ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value === 'yes')}
              row
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        );

      case 'text':
        return (
          <TextField
            key={field.id}
            label={field.label}
            value={value || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            multiline
            rows={3}
            fullWidth
            required={field.required}
            placeholder={field.config?.placeholder}
            inputProps={{ maxLength: field.config?.maxLength || 500 }}
            helperText={field.helpText}
            sx={{ mb: 3 }}
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/parent/dashboard')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          Daily Check-In for {child?.firstName || 'Child'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {template?.displayName || 'Parent Daily Observation'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please answer based on your observations of {child?.firstName || 'your child'} today.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSubmit}>
          {template?.fields
            ?.sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(field => renderField(field))}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              variant="outlined"
              onClick={() => navigate('/parent/dashboard')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Check-In'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CheckInForChild;
